import React, { useState, useRef } from "react";
import {
  Typography,
  Stack,
  Box,
  Tab,
  Tabs,
  Button,
} from "@mui/material";
import {
  Add as AddIcon,
  Settings as SettingsIcon,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import { SelectClientBanner } from "@/components";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { tabSx } from "@/utils/customs-helpers";
import { tokens } from "@/tokens";

// Import duty columns manager and authority timeline from product catalogue
import DutyColumnsManager, { DutyColumnsManagerRef } from "../product-catalogue/components/duty-columns-manager";
import AuthorityTimeline from "../product-catalogue/components/authority-timeline";

const DutyColumnsPage: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);

  const [activeTab, setActiveTab] = useState(0);

  // Ref for DutyColumnsManager
  const dutyColumnsRef = useRef<DutyColumnsManagerRef>(null);

  const canSelectClients = React.useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const isTDAdminTCD = React.useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
  }, [user, selectedTenant]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (canSelectClients && !selectedClient && !isTDAdminTCD) {
    return <SelectClientBanner />;
  }

  const getPageTitle = () => {
    switch (activeTab) {
      case 0: return 'Duty Columns';
      case 1: return 'Authority Timeline';
      default: return 'Duty Columns';
    }
  };

  const getPageDescription = () => {
    switch (activeTab) {
      case 0: return 'Create and manage duty columns with custom tariff rules for enhanced product calculations';
      case 1: return 'Visualize the evolution of trade policies across different authorities over time';
      default: return 'Manage duty columns and authority timeline';
    }
  };

  return (
    <Stack spacing={0} sx={{
      margin: {
        xs: '-6px -10px 0 -10px',
        md: '-12px -20px 0 -20px'
      },
      width: {
        xs: 'calc(100% + 20px)',
        md: 'calc(100% + 40px)'
      }
    }}>
      {/* Main Tabs - Edge to edge */}
      <Box sx={{ borderBottom: `1px solid ${tokens.border.default}`, margin: 0, padding: 0, width: '100%', bgcolor: tokens.bg.surface }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ minHeight: 44, px: 3, margin: 0 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 44,
              '& .MuiTabs-indicator': {
                backgroundColor: '#0a0a0f',
                height: 2,
                bottom: 0,
              },
            }}
          >
            <Tab
              label="Duty Columns"
              icon={<SettingsIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              sx={{ ...tabSx, gap: 0.75 }}
            />
            <Tab
              label="Authority Timeline"
              icon={<TimelineIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              sx={{ ...tabSx, gap: 0.75 }}
            />
          </Tabs>
        </Stack>
      </Box>

      {/* Header Section (Title & Desc + Action Buttons) */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mt: 2, px: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={tokens.fontWeight.heavy} sx={{ mb: 0.5, color: tokens.text.primary }}>
            {getPageTitle()}
          </Typography>
          <Typography variant="body1" sx={{ color: tokens.text.secondary }}>
            {getPageDescription()}
          </Typography>
        </Box>

        {/* Create Column button for Duty Columns tab */}
        {activeTab === 0 && (
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={() => dutyColumnsRef.current?.openCreateForm()}
            sx={{
              background: `linear-gradient(180deg, rgba(255, 255, 255, 0.16) 0%, rgba(255, 255, 255, 0) 100%), ${tokens.brand.primary}`,
              border: `1px solid ${tokens.bg.surface}`,
              boxShadow: "inset 0px 4px 46px rgba(255, 255, 255, 0.16)",
              borderRadius: tokens.radius.md,
              color: tokens.bg.surface,
              height: 36,
              padding: '6px 16px',
              fontSize: tokens.fontSize.lg,
              "&:hover": {
                background: tokens.brand.primaryHover,
              },
              fontWeight: tokens.fontWeight.bold,
            }}
          >
            Create Column
          </Button>
        )}
      </Stack>

      {/* Tab Content */}
      <Box sx={{ px: 3, pt: 2, pb: 3 }}>
        {activeTab === 0 && <DutyColumnsManager ref={dutyColumnsRef} />}
        {activeTab === 1 && <AuthorityTimeline />}
      </Box>
    </Stack>
  );
};

export default DutyColumnsPage;
